/**
 * FR-142 · Quên mật khẩu và Đặt lại mật khẩu.
 * Endpoint quên mật khẩu luôn trả 204 nên màn hình không tiết lộ email tồn tại.
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, ensureCsrfCookie, useLoiApi } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { Field } from '../../components/Field';
import { PasswordStrength } from '../../components/PasswordStrength';
import { AuthLayout, FormAlert } from './AuthLayout';

export function ForgotPasswordPage() {
  const t = useT();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await ensureCsrfCookie();
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={t('auth.forgot.title')}
      lede={t('auth.forgot.lede')}
      foot={
        <Link className="small" to="/login">
          {t('auth.2fa.backToLogin')}
        </Link>
      }
    >
      {sent ? (
        <FormAlert tone="ok">{t('auth.forgot.sent')}</FormAlert>
      ) : (
        <form
          className="stack"
          onChange={xoaLoiKhiGo.onChange}
          onSubmit={submit}
          data-od-id="form-forgot"
        >
          {error ? <FormAlert>{error.message}</FormAlert> : null}
          <Field
            label={t('auth.login.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
            {busy ? t('common.processing') : t('auth.forgot.submit')}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();
  const [mismatch, setMismatch] = useState(false);

  /*
   * OQ-243 — so khớp NGAY khi gõ, không đợi bấm nút.
   *
   * Suy từ giá trị hiện tại thay vì đọc `mismatch`: state ấy chỉ đổi lúc submit,
   * nên sau một lần báo lệch nó ở lại đỏ trong khi người dùng đã gõ đúng, và
   * chỉ tắt khi họ bấm nút thêm lần nữa.
   *
   * `confirm.length > 0` — chưa gõ gì thì chưa có gì để sai.
   */
  const lech = confirm.length > 0 && password !== confirm;

  async function submit(event: FormEvent) {
    event.preventDefault();
    /* Ô rỗng cũng phải chặn — `lech` cố ý bỏ qua ca ấy để không đỏ khi chưa gõ. */
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    setBusy(true);
    setError(null);
    try {
      await ensureCsrfCookie();
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthLayout title={t('auth.reset.title')}>
        <div className="stack">
          <FormAlert tone="ok">{t('auth.reset.done')}</FormAlert>
          <button
            className="btn btn-primary btn-block"
            type="button"
            onClick={() => navigate('/login', { replace: true })}
          >
            {t('auth.reset.goLogin')}
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.reset.title')}
      lede={t('auth.reset.lede')}
      foot={
        <Link className="small" to="/login">
          {t('auth.2fa.backToLogin')}
        </Link>
      }
    >
      <form
        className="stack"
        onChange={xoaLoiKhiGo.onChange}
        onSubmit={submit}
        data-od-id="form-reset"
      >
        {error ? <FormAlert>{error.message}</FormAlert> : null}
        {!token ? <FormAlert>{t('state.notFound.body')}</FormAlert> : null}

        <Field
          label={t('auth.reset.newPassword')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          autoFocus
          required
          error={error?.fieldMessage('newPassword') ?? error?.fieldMessage('password')}
          trailing={<PasswordStrength value={password} />}
        />

        <Field
          label={t('auth.reset.confirmPassword')}
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          required
          error={lech || mismatch ? t('auth.mismatch') : undefined}
        />

        <button className="btn btn-primary btn-block" type="submit" disabled={busy || !token}>
          {busy ? t('common.processing') : t('auth.reset.submit')}
        </button>
      </form>
    </AuthLayout>
  );
}
